import React, { useState, useEffect } from "react";

import { Box, Button, CircularProgress, Grid, Typography, TextField, IconButton, Paper, Snackbar, Alert } from "@mui/material";
import PWAHeader from "../../common/PWAHeader";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../../common/PrimaryButton";
import LoadingProgressBar from "../../common/LoadingProgressBar";
import DatePickerRow from "../../common/DatePickerRow"; // Import the DatePickerRow component
import { fetchDoctorAppointmentSlots,saveDoctorAppointmentSlots } from "../../apiservices/ApiService";
import { getCoachId } from "../../common/Utils";

const Fsn8DoctorTimeSlotBooking = ({ onTap }) => {
  const navigate = useNavigate();
  const [slotData, setSlotData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [appointmentReason, setAppointmentReason] = useState("");
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isLoading, setIsLoading] = useState(false);

  const fetchSlots = async (date) => {
    setLoadingSlots(true);
    setIsLoading(true);
    try {
      const params = { goqiiCoachId: getCoachId(), queryDate: date };
      const data = await fetchDoctorAppointmentSlots(params);

      if (data && data.slotdata) {
        setSlotData(data.slotdata);
      } else {
        setSlotData([]);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlotData([]);
    } finally {
      setLoadingSlots(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);
    fetchSlots(today);
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchSlots(date);
    setSelectedSlot("");
  };

  const handleNextWeek = () => {
    setCurrentWeek((prev) => prev + 1);
  };

  const handlePreviousWeek = () => {
    if (currentWeek > 0) setCurrentWeek((prev) => prev - 1);
  };

  const handleConfirm = async () => {
    if (!selectedSlot || !appointmentReason) {
      setSnackbar({ open: true, message: "Please select a slot and provide a reason.", severity: "warning" });
      return;
    }

    setLoadingConfirm(true);
    try {
      const payload = {
        type: "",
        appointmentDate: selectedDate,
        appointmentReason,
        appointmentTime: selectedSlot,
        memberId: "",
      };

      await saveDoctorAppointmentSlots(payload);
      setSnackbar({ open: true, message: "Appointment confirmed successfully!", severity: "success" });

      setTimeout(() => {
        handleBackButtonPress();
      }, 1500);
    } catch (error) {
      console.error("Error saving appointment:", error);
      setSnackbar({ open: true, message: "Failed to confirm appointment. Please try again.", severity: "error" });
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleBackButtonPress = () => {
    if (window.Android) {
      window.Android.onBackPressed();
    } else {
      navigate(-1);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div style={{ height: "100%" }}>
      <PWAHeader title={onTap?.FAI?.title || "Book a Phone Consultation"} />
      {isLoading && <LoadingProgressBar />}

      <Paper
        elevation={3}
        sx={{
          padding: 3,
          maxWidth: 600,
          margin: "0 auto",
          borderRadius: 3,
          backgroundColor: "rgba(255, 152, 0, 0.11)",
        }}
      >
        {/* Date Picker Row Integration */}
        <DatePickerRow
          selectedDate={selectedDate}
          onDateSelect={handleDateChange}
          currentWeek={currentWeek}
          setCurrentWeek={setCurrentWeek}
          restrictPastDates={true} // Restrict past dates for appointment booking
          restrictFutureDates={false} // Allow future dates
        />

        {/* Time Slots */}
        <Box>
          {loadingSlots ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 80,
              }}
            >
              <CircularProgress size={20} />
            </Box>
          ) : slotData.length > 0 ? (
            slotData.map((dataItem, index) => (
              <Grid container spacing={1} key={index} sx={{ marginBottom: 2 }}>
                {dataItem.slots.map((slot, slotIndex) => (
                  <Grid item xs={6} sm={4} key={slotIndex}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setSelectedSlot(slot)}
                      fullWidth
                      sx={{
                        borderRadius: 4,
                        boxShadow:
                          selectedSlot === slot
                            ? "0px 0px 5px 0px #681517"
                            : "0 0 3px 0px #681517",
                        textTransform: "none",
                        fontSize: 10,
                        color: selectedSlot === slot ? "white" : "#681517",
                        backgroundColor:
                          selectedSlot === slot ? "rgba(255, 152, 0)" : "#fff",
                        borderColor:
                          selectedSlot === slot
                            ? "rgba(255, 152, 0)"
                            : "#681517",
                      }}
                    >
                      {slot}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            ))
          ) : (
            <Typography color="error" align="center" sx={{ fontSize: 12 }}>
              No slots available for this date.
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant="body2"
            align="center"
            color="#681517"
            sx={{ fontSize: 10 }}
          >
            Time Zone: <b>+05:30, Asia/Kolkata, India Standard Time</b>
          </Typography>
          <TextField
            placeholder="Describe your health concern"
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            value={appointmentReason}
            onChange={(e) => setAppointmentReason(e.target.value)}
            sx={{
              marginTop: 2,
              marginBottom: 2,
              borderRadius: 2,
              fontSize: 10,
              border: "1px solid #681517",
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                border: "1px solid #681517",
              },
              "& .MuiOutlinedInput-focused": { border: "1px solid #681517" },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                    borderColor: '#681517',
                },
            },
            '& .MuiInputLabel-root.Mui-focused': {
                color: '#681517',
            }, 
            }}
          />
          <PrimaryButton onClick={handleConfirm} label="Book" />
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </div>
  );
};

export default Fsn8DoctorTimeSlotBooking;